import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FormPanelProps, FormSwitch, FormTextField } from "./formFields";

const LANGUAGE_OPTIONS = ["de", "en", "fr", "es", "it", "nl", "pl", "cs", "sk"];

const DATE_FORMAT_OPTIONS = [
  "MM/dd/yy HH:mm:ss",
  "dd.MM.yyyy HH:mm:ss",
  "yyyy-MM-dd HH:mm:ss",
  "dd/MM/yyyy HH:mm:ss",
];

/**
 * Start pages MyGeotab itself knows (`defaultPage` is the hash of the MyGeotab URL, default
 * `map`). Other values that are already set on a user are still shown for that user.
 */
const DEFAULT_PAGE_OPTIONS = ["map", "dashboard"];

function getBaseTimeZones(): string[] {
  const intlWithSupportedValues = Intl as unknown as {
    supportedValuesOf?: (key: string) => string[];
  };
  if (typeof intlWithSupportedValues.supportedValuesOf === "function") {
    return intlWithSupportedValues.supportedValuesOf("timeZone");
  }
  return ["Europe/Berlin", "UTC", "America/New_York"];
}

const BASE_TIME_ZONES = getBaseTimeZones();

/** Select that also offers the current value when it is not one of the standard options. */
function OptionSelect({
  form,
  setField,
  field,
  options,
  label,
  getOptionLabel = option => option,
}: Pick<FormPanelProps, "form" | "setField"> & {
  field: "language" | "dateFormat" | "defaultPage";
  options: string[];
  label: string;
  /** Display text of a value; the value itself is what gets saved. */
  getOptionLabel?: (option: string) => string;
}) {
  const current = form[field];
  return (
    <FormTextField form={form} setField={setField} field={field} label={label} select>
      {current && !options.includes(current) ? (
        <MenuItem value={current}>{getOptionLabel(current)}</MenuItem>
      ) : null}
      {options.map(option => (
        <MenuItem key={option} value={option}>
          {getOptionLabel(option)}
        </MenuItem>
      ))}
    </FormTextField>
  );
}

export function UiSettingsTab({ form, setField, isMobile }: FormPanelProps) {
  const { t, i18n } = useTranslation();
  const languageNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language], { type: "language" });
    } catch {
      return null;
    }
  }, [i18n.language]);
  const bind = { form, setField };
  const direction = isMobile ? "column" : "row";
  const timeZoneOptions = useMemo(
    () =>
      !form.timeZoneId || BASE_TIME_ZONES.includes(form.timeZoneId)
        ? BASE_TIME_ZONES
        : [form.timeZoneId, ...BASE_TIME_ZONES],
    [form.timeZoneId],
  );
  return (
    <Stack spacing={1.25}>
      <Stack direction={direction} spacing={1.25}>
        <OptionSelect
          {...bind}
          field="language"
          options={LANGUAGE_OPTIONS}
          label={t("users.language")}
          getOptionLabel={code => languageNames?.of(code) ?? code}
        />
        <Autocomplete
          options={timeZoneOptions}
          value={form.timeZoneId}
          onChange={(_event, value) => setField("timeZoneId", value ?? "")}
          autoHighlight
          fullWidth
          renderInput={params => (
            <TextField {...params} label={t("users.timeZoneId")} size="small" />
          )}
        />
      </Stack>
      <Stack direction={direction} spacing={1.25}>
        <OptionSelect
          {...bind}
          field="dateFormat"
          options={DATE_FORMAT_OPTIONS}
          label={t("users.dateFormat")}
        />
        <OptionSelect
          {...bind}
          field="defaultPage"
          options={DEFAULT_PAGE_OPTIONS}
          label={t("users.defaultPage")}
          getOptionLabel={page => t(`users.defaultPages.${page}`, { defaultValue: page })}
        />
      </Stack>
      <FormSwitch {...bind} field="isMetric" label={t("users.isMetric")} />
    </Stack>
  );
}
